import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { CheckCircle2Icon } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/button/button'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { activateCasesMutation } from '@/lib/queries/business/v1/case/active/mutations'
import { archiveCasesMutation } from '@/lib/queries/business/v1/case/archive/mutations'
import { cn } from '@/lib/utils'

export interface CaseStatusActionToastContentProps {
  caseIds: number[]
  changedStatus: 'active' | 'inactive'
  isPlural: boolean
}

export const CaseStatusActionToastContent = ({
  caseIds,
  changedStatus,
  isPlural,
}: CaseStatusActionToastContentProps) => {
  const { mutateAsync: archiveCases, isPending: isArchivingCases } = useMutation(archiveCasesMutation)
  const { mutateAsync: activateCases, isPending: isActivatingCases } = useMutation(activateCasesMutation)

  const handleClickUndoButton = async () => {
    if (changedStatus === 'active') {
      await archiveCases(
        {
          caseIds,
        },
        {
          onSuccess: async () => {
            toast.dismiss()
            toast.success('Successfully restored the inactive state.')
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v4.case.search.index],
            })
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v2.user.index],
            })
          },
        }
      )
    } else if (changedStatus === 'inactive') {
      await activateCases(
        {
          caseIds,
        },
        {
          onSuccess: async () => {
            toast.dismiss()
            toast.success('Successfully restored the active state.')
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v4.case.search.index],
            })
            await queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v2.user.index],
            })
          },
        }
      )
    }
  }

  const isLoadingUndoButton = isArchivingCases || isActivatingCases

  return (
    <div className={cn('flex items-center space-x-4')}>
      <span>
        <CheckCircle2Icon size={24} />
      </span>
      <div className={cn('flex flex-col space-y-1.5')}>
        <span className={cn('text-base leading-5 text-foreground', 'whitespace-nowrap')}>
          Selected {isPlural ? 'cases have' : 'case has'} been{' '}
          {changedStatus === 'active' ? 'activated' : 'inactivated'}
        </span>
        <span className={cn('text-sm text-placeholder', 'whitespace-nowrap')}>
          You can check {isPlural ? 'them' : 'it'} in the {changedStatus === 'active' ? 'Active' : 'Inactive'} Cases
          tab.
        </span>
      </div>
      <Button
        type="button"
        variant="black"
        className={cn('px-4 py-2')}
        loading={isLoadingUndoButton}
        onClick={handleClickUndoButton}
      >
        Undo
      </Button>
    </div>
  )
}
